<template>
  <div class="card mb-5 mb-xxl-8">
    <div class="card mb-5 mb-xl-10">
      <div class="card-header flex-wrap border-0 pt-6 pb-0">
        <div class="card-title">
          <h3 class="card-label">{{ $t('shop.low_rotation_products') }}</h3>
          <span class="text-muted mt-1 fw-bold fs-7 ms-2">Sans mouvement de stock depuis</span>
        </div>
        <div class="card-toolbar d-flex gap-3 align-items-center flex-wrap">
          <select v-model.number="days" @change="reset" class="form-select form-select-sm border-primary" style="width:110px;">
            <option v-for="d in dayOptions" :key="d" :value="d">{{ d }} jours</option>
          </select>
          <select v-model.number="limit" @change="reset" class="form-select form-select-sm border-primary" style="width:130px;">
            <option v-for="l in limitOptions" :key="l" :value="l">{{ l }} / page</option>
          </select>
          <span v-if="total !== null" class="text-muted fs-7">{{ total }} produit(s)</span>
          <button
            v-if="$hasShopModule('ecommerce.promotions')"
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="selectedIds.length === 0"
            @click="createPromotionFromSelection"
          >
            Mettre en promotion<span v-if="selectedIds.length"> ({{ selectedIds.length }})</span>
          </button>
        </div>
      </div>

      <div class="card-body px-0 p-0" style="max-height: 520px; overflow-y: auto;" ref="scrollContainer">
        <!-- État vide -->
        <div v-if="!isLoading && products.length === 0" class="text-center py-10">
          <img src="@/assets/media/pos/Empty2.png" alt="" class="h-150px mb-3" />
          <div class="text-gray-400 fs-5 fw-bold">Aucun produit stagnant sur cette période</div>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-row-bordered align-middle fs-6 gy-3 mb-0">
            <thead>
              <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                <th class="ps-6" style="width:36px;">
                  <input type="checkbox" class="form-check-input" :checked="allSelected" @change="toggleSelectAll" />
                </th>
                <th class="min-w-200px">Produit</th>
                <th class="min-w-80px">Stock</th>
                <th class="min-w-80px">Sécurité</th>
                <th class="min-w-80px">Alerte</th>
                <th class="min-w-150px">Dernière vente</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in products" :key="p.id + p.shop_id">
                <td class="ps-6">
                  <input type="checkbox" class="form-check-input" :checked="selectedIds.includes(p.id)" @change="toggleSelect(p.id)" />
                </td>
                <td class="fw-bold">{{ p.name }}</td>
                <td>
                  <span class="badge"
                    :class="p.stock_variable <= 0 ? 'badge-light-danger' :
                             p.stock_variable <= p.stock_security && p.stock_security > 0 ? 'badge-light-warning' :
                             p.stock_variable <= p.stock_alert && p.stock_alert > 0 ? 'badge-light-info' : 'badge-light-success'">
                    {{ p.stock_variable }}
                  </span>
                </td>
                <td class="text-muted">{{ p.stock_security }}</td>
                <td class="text-muted">{{ p.stock_alert }}</td>
                <td>
                  <span v-if="p.lastSale">
                    <span class="text-muted me-1">{{ p.lastSale }}</span>
                    <span class="fw-bold text-warning">({{ fromNow(p.lastSale) }})</span>
                  </span>
                  <span v-else class="badge badge-light-danger">Jamais vendu</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Sentinelle scroll infini -->
          <div ref="sentinel" class="py-4 text-center">
            <div v-if="isLoading" class="spinner-border spinner-border-sm text-primary"></div>
            <span v-else-if="!hasMore && products.length > 0" class="text-muted fs-8">— Fin de la liste —</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BASE_URL } from '@/services/api'

export default {
  name: 'ShopStagnantProducts',
  data() {
    return {
      products: [],
      isLoading: false,
      hasMore: true,
      total: null,
      offset: 0,
      days: 10,
      limit: 25,
      dayOptions: [3, 5, 10, 15, 20, 30, 60],
      limitOptions: [10, 25, 50, 100],
      observer: null,
      selectedIds: [],
    }
  },
  computed: {
    locale() { return this.$i18n?.locale },
    allSelected() {
      return this.products.length > 0 && this.selectedIds.length === this.products.length
    },
  },
  mounted() {
    this.fetchProducts()
    this.$nextTick(() => this.initObserver())
  },
  beforeUnmount() {
    this.observer?.disconnect()
  },
  watch: {
    '$route.params.id'(newId, oldId) {
      // Vue Router réutilise ce composant quand on passe de la fiche d'une
      // boutique à une autre (même route, seul :id change) — sans ce watcher,
      // la liste garde les produits stagnants de l'ancienne boutique.
      if (newId && newId !== oldId) this.reset()
    },
  },
  methods: {
    fromNow(dateStr) {
      return moment(dateStr, 'DD-MM-YYYY').locale(this.locale).fromNow()
    },

    reset() {
      this.products = []
      this.offset   = 0
      this.hasMore  = true
      this.total    = null
      this.selectedIds = []
      this.fetchProducts()
    },

    toggleSelect(id) {
      const i = this.selectedIds.indexOf(id)
      if (i === -1) this.selectedIds.push(id)
      else this.selectedIds.splice(i, 1)
    },

    toggleSelectAll() {
      // "Tout" = tous les produits stagnants actuellement chargés à l'écran
      // (la liste défile à l'infini) — pas le catalogue entier.
      this.selectedIds = this.allSelected ? [] : this.products.map((p) => p.id)
    },

    createPromotionFromSelection() {
      const products = this.products
        .filter((p) => this.selectedIds.includes(p.id))
        .map((p) => ({ id: p.id, name: p.name }))
      this.$store.commit('promotion/SET_PENDING_PRODUCTS', products)
      this.$router.push({ name: 'promotion-create' })
    },

    initObserver() {
      if (!this.$refs.sentinel || !this.$refs.scrollContainer) return
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.isLoading && this.hasMore) {
          this.fetchProducts()
        }
      }, { root: this.$refs.scrollContainer, threshold: 0.1 })
      this.observer.observe(this.$refs.sentinel)
    },

    async fetchProducts() {
      if (this.isLoading || !this.hasMore) return
      this.isLoading = true
      try {
        const AUTH_TOKEN = Ls.get('auth.token')
        const res = await window.axios.get(
          BASE_URL + '/get/shop/' + this.$route.params.id + '/stagnant/products',
          {
            params: { days: this.days, limit: this.limit, offset: this.offset },
            headers: { Authorization: 'Bearer ' + AUTH_TOKEN },
          }
        )
        const { data, total, hasMore } = res.data
        this.products.push(...data)
        this.total   = total
        this.hasMore = hasMore
        this.offset += this.limit
      } catch (e) {
        this.hasMore = false
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
